import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { formatViews, getCategoryName, getRelatedMovies } from "@/utils/catalog";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import usePageMeta from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { id } = useParams();
  const movie = movies.find((item) => String(item.id) === id);
  const [shareTip, setShareTip] = useState("");

  usePageMeta({
    title: movie ? `${movie.title} - 电影天堂影片详情` : "影片详情 - 电影天堂",
    description: movie
      ? `查看${movie.title}的详细介绍、导演主演信息、剧情简介及相关推荐。`
      : "查看电影详细介绍与相关推荐。",
    keywords: movie
      ? `电影天堂,${movie.title},${movie.genres.map(getCategoryName).join(",")},电影详情`
      : "电影天堂,电影详情",
  });

  if (!movie) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <i className="ri-film-line text-6xl text-foreground-500" />
          <h1 className="mt-5 text-2xl font-bold text-foreground-50">未找到该影片</h1>
          <p className="mt-2 text-sm text-foreground-400">影片可能已下线或链接有误</p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-medium text-background-950 transition hover:bg-accent-400"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedMovies(movies, movie, 6);

  const handleCopy = async () => {
    const url = `${window.location.origin}${__BASE_PATH__}detail/${movie.id}`.replace(/\/+/g, "/").replace(":/", "://");
    try {
      await navigator.clipboard.writeText(url);
      setShareTip("链接已复制到剪贴板");
    } catch {
      setShareTip("复制失败，请手动复制地址栏链接");
    }
    setTimeout(() => setShareTip(""), 2500);
  };

  const socials = [
    { icon: "ri-wechat-line", label: "微信" },
    { icon: "ri-weibo-line", label: "微博" },
    { icon: "ri-qq-line", label: "QQ" },
  ];

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genres.map(getCategoryName).join(" · ") },
    { label: "地区", value: movie.region },
    { label: "上映日期", value: movie.releaseDate },
    { label: "片长", value: `${movie.duration} 分钟` },
    { label: "语言", value: "国语 / 原声" },
    { label: "点击量", value: formatViews(movie.views) },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-6 flex flex-wrap items-center gap-1 text-sm text-foreground-400" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${movie.genres[0]}`} className="hover:text-accent-400">
              {getCategoryName(movie.genres[0])}电影
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-100">{movie.title}</span>
          </nav>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="mx-auto w-[220px] shrink-0 sm:w-[260px] lg:col-span-4 lg:mx-0 lg:w-full lg:max-w-[320px]">
              <div className="overflow-hidden rounded-lg ring-1 ring-background-200/70">
                <img
                  src={movie.poster}
                  alt={`${movie.title}电影海报`}
                  title={`${movie.title} - 电影天堂`}
                  className="h-full w-full object-top"
                />
              </div>
            </div>

            <div className="lg:col-span-8">
              <h1 className="text-2xl font-bold text-foreground-50 md:text-4xl">{movie.title}</h1>
              <p className="mt-2 text-sm text-foreground-400 md:text-base">{movie.enTitle}</p>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1 rounded-md bg-accent-500 px-3 py-1 text-sm font-bold text-background-950">
                  <i className="ri-star-fill" />
                  {movie.rating.toFixed(1)}
                </span>
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${g}`}
                    className="rounded-md bg-background-100 px-3 py-1 text-sm text-foreground-200 ring-1 ring-background-200/70 transition hover:text-accent-400"
                  >
                    {getCategoryName(g)}
                  </Link>
                ))}
                <span className="flex items-center gap-1 text-sm text-foreground-400">
                  <i className="ri-fire-line text-accent-500" />
                  {formatViews(movie.views)} 次浏览
                </span>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg bg-background-100 p-4 ring-1 ring-background-200/70 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-2 text-sm">
                    <span className="shrink-0 text-foreground-500">{row.label}</span>
                    <span className="text-foreground-100">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <h2 className="flex items-center gap-2 text-lg font-medium text-foreground-50">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  剧情简介
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-300 md:text-base">
                  {movie.synopsis}
                </p>
              </div>

              <div className="mt-6">
                <h2 className="flex items-center gap-2 text-lg font-medium text-foreground-50">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  分享影片
                </h2>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-background-100 px-4 py-2 text-sm text-foreground-100 ring-1 ring-background-200/70 transition hover:text-accent-400"
                  >
                    <i className="ri-link" />
                    复制链接
                  </button>
                  {socials.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      disabled
                      title="社交分享为演示功能"
                      className="flex cursor-not-allowed items-center gap-2 whitespace-nowrap rounded-md bg-background-100 px-4 py-2 text-sm text-foreground-500 ring-1 ring-background-200/70"
                    >
                      <i className={item.icon} />
                      {item.label}
                      <span className="rounded bg-background-200 px-1 text-[10px] text-foreground-400">演示</span>
                    </button>
                  ))}
                </div>
                {shareTip && (
                  <p className="mt-3 text-sm text-accent-500">{shareTip}</p>
                )}
              </div>
            </div>
          </div>

          <section className="mt-14">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-50 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              相关推荐
            </h2>
            <p className="mt-1 text-sm text-foreground-400">根据影片类型为你推荐</p>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>

          <div className="mt-10 flex justify-center">
            <Link
              to={`/category/${movie.genres[0]}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-100 transition hover:border-accent-500/70 hover:text-accent-400"
            >
              <i className="ri-arrow-left-line" />
              返回{getCategoryName(movie.genres[0])}电影
            </Link>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}